/* Interactive showcase – scroll-scrubbed video + product hotspots */
(function () {
  'use strict';

  var clamp = function (v, min, max) {
    return Math.max(min, Math.min(max, v));
  };

  // The video is scrubbed over the first SCRUB_END of the scroll track. The
  // remaining tail is a dwell zone: the car stays clean and pinned while the
  // hotspots are on screen, so there is time to actually click one.
  var SCRUB_END = 0.8;

  // The playhead is eased toward the scroll target on a rAF loop instead of
  // being slammed to it on every scroll event. Touch scrolling fires bursts of
  // jittery deltas, and one decode per delta is what makes the scrub look
  // steppy on phones; easing turns those bursts into a single smooth glide.
  var SEEK_EASE = 0.18;
  // Below this the playhead is close enough that another seek would only cost a
  // decode without changing the visible frame.
  var SEEK_EPSILON = 0.008;
  // A fling or an anchor jump is not something to ease through — past this gap
  // we snap, so the frame never trails the scroll by a visible amount.
  var SEEK_SNAP = 0.4;

  class InteractiveShowcase extends HTMLElement {
    connectedCallback() {
      // Tolerate the pre-portrait markup, where the single video carried a
      // valueless data-ishowcase-video: a cached asset paired with fresh markup
      // (or the reverse) would otherwise find nothing and fall back to static.
      this.desktopVideo =
        this.querySelector('[data-ishowcase-video="desktop"]') ||
        this.querySelector('[data-ishowcase-video=""]');
      this.mobileVideo = this.querySelector('[data-ishowcase-video="mobile"]');
      this.pin = this.querySelector('.ishowcase__pin');
      this.mobileQuery = window.matchMedia('(max-width: 767px)');
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.isPlaceholder = this.classList.contains('is-placeholder');
      // Merchants can opt out: the animation only advances while the visitor
      // scrolls, so honouring the OS flag is a choice rather than a requirement.
      this.honourReducedMotion = this.dataset.respectReducedMotion !== 'false';

      this.setupPopups();
      this.applyMode();
      this.mobileQuery.addEventListener('change', this.applyMode.bind(this));
    }

    // The cut that belongs to the current breakpoint. Returns null on phones
    // when no portrait file was provided, which drops the section into static
    // mode — the behaviour mobile always had before the portrait cut existed.
    activeVideo() {
      if (this.mobileQuery.matches) return this.mobileVideo;
      return this.desktopVideo;
    }

    prefersStatic() {
      return this.honourReducedMotion && this.reduceMotion.matches;
    }

    applyMode() {
      var next = this.activeVideo();

      if (!next || this.prefersStatic()) {
        this.enableStaticMode();
        return;
      }
      // Already scrubbing this exact element — nothing to rebuild.
      if (this._scrubActive && this.video === next) return;

      this.enableScrubMode(next);
    }

    detachScroll() {
      this.stopSeekLoop();
      if (!this._onScroll) return;
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
      this._onScroll = null;
      this._onResize = null;
    }

    enableStaticMode() {
      this._scrubActive = false;
      this.classList.add('is-static', 'is-revealed');
      this.detachScroll();
    }

    enableScrubMode(video) {
      // Switching cuts across the breakpoint re-enters here, so drop the
      // previous listener before wiring a new one.
      this.detachScroll();
      this.classList.remove('is-static');
      this.video = video || this.activeVideo();
      this._scrubActive = true;
      this.duration = 0;
      this.ticking = false;

      var self = this;
      this.video.muted = true;
      // The inactive cut ships with preload="none" so it costs nothing until it
      // is the one on screen.
      if (this.video.preload !== 'auto') this.video.preload = 'auto';
      this.video.pause();

      var ready = function () {
        self.duration = self.video.duration || 0;
        self.update();
      };
      if (this.video.readyState >= 1) {
        ready();
      } else {
        this.video.addEventListener('loadedmetadata', ready, { once: true });
      }
      // Nudge loading
      try { this.video.load(); } catch (e) {}

      this._onScroll = function () {
        if (!self.ticking) {
          window.requestAnimationFrame(function () {
            self.update();
            self.ticking = false;
          });
          self.ticking = true;
        }
      };
      // Mobile browsers fire resize every time the URL bar slides in or out.
      // Re-measuring there is both pointless (the pin is sized in svh, so its
      // geometry did not change) and harmful — it lands mid-gesture. Only a
      // width change, i.e. a real resize or a rotation, is worth reacting to.
      this._lastWidth = window.innerWidth;
      this._onResize = function () {
        if (window.innerWidth === self._lastWidth) return;
        self._lastWidth = window.innerWidth;
        self.update();
      };
      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize, { passive: true });
      this.update();
    }

    // Walks the playhead toward this.targetTime one frame at a time. Runs only
    // while there is ground left to cover, then parks itself.
    startSeekLoop() {
      if (this._seekRaf) return;
      var self = this;
      var step = function () {
        self._seekRaf = 0;
        var video = self.video;
        if (!video || !self._scrubActive) return;

        // A seek already in flight: let it land, keep the loop alive so the
        // newest target is picked up on the next frame.
        if (video.seeking) {
          self._seekRaf = window.requestAnimationFrame(step);
          return;
        }

        var delta = self.targetTime - video.currentTime;
        if (Math.abs(delta) < SEEK_EPSILON) return;

        video.currentTime =
          Math.abs(delta) > SEEK_SNAP ? self.targetTime : video.currentTime + delta * SEEK_EASE;
        self._seekRaf = window.requestAnimationFrame(step);
      };
      this._seekRaf = window.requestAnimationFrame(step);
    }

    stopSeekLoop() {
      if (!this._seekRaf) return;
      window.cancelAnimationFrame(this._seekRaf);
      this._seekRaf = 0;
    }

    update() {
      var rect = this.getBoundingClientRect();
      // Measure the pinned stage rather than assuming it fills the viewport —
      // on phones it is capped, so the sticky element unsticks after
      // (track - pinHeight), which is exactly the distance to map progress over.
      var pinHeight = (this.pin && this.pin.offsetHeight) || window.innerHeight;
      var scrollable = this.offsetHeight - pinHeight;
      if (scrollable <= 0) return;
      var progress = clamp(-rect.top / scrollable, 0, 1);
      var videoProgress = clamp(progress / SCRUB_END, 0, 1);

      if (this.duration) {
        // Stop a hair short of the duration: seeking to exactly duration can
        // land past the last frame and blank the video in some browsers.
        this.targetTime = videoProgress * (this.duration - 0.02);
        this.startSeekLoop();
      }

      var revealed = videoProgress >= 1;
      if (revealed !== this._revealed) {
        this._revealed = revealed;
        this.classList.toggle('is-revealed', revealed);
        // Scrolling back up into the animation dismisses any open product card
        if (!revealed) this.closePopups();
      }
    }

    setupPopups() {
      var self = this;
      this.querySelectorAll('[data-hotspot]').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          self.togglePopup(btn.getAttribute('data-hotspot'), btn);
        });
      });
      this.querySelectorAll('[data-popup-close]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.closePopups();
        });
      });
      // Close on Escape
      this._onKey = function (e) {
        if (e.key === 'Escape') self.closePopups();
      };
      document.addEventListener('keydown', this._onKey);
      // Close when clicking outside a popup / hotspot
      this._onDocClick = function (e) {
        if (!e.target.closest('.ishowcase__popup') && !e.target.closest('[data-hotspot]')) {
          self.closePopups();
        }
      };
      document.addEventListener('click', this._onDocClick);
    }

    togglePopup(id, btn) {
      var popup = this.querySelector('[data-popup="' + id + '"]');
      var isOpen = popup && popup.classList.contains('is-open');
      this.closePopups();
      if (popup && !isOpen) {
        popup.classList.add('is-open');
        if (btn) btn.setAttribute('aria-expanded', 'true');
      }
    }

    closePopups() {
      this.querySelectorAll('.ishowcase__popup.is-open').forEach(function (p) {
        p.classList.remove('is-open');
      });
      this.querySelectorAll('[data-hotspot][aria-expanded="true"]').forEach(function (b) {
        b.setAttribute('aria-expanded', 'false');
      });
    }

    disconnectedCallback() {
      this.detachScroll();
      if (this._onKey) document.removeEventListener('keydown', this._onKey);
      if (this._onDocClick) document.removeEventListener('click', this._onDocClick);
    }
  }

  if (!customElements.get('interactive-showcase')) {
    customElements.define('interactive-showcase', InteractiveShowcase);
  }
})();
